import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldOff, RefreshCw, CalendarDays, Camera, Landmark, HandCoins, PackageCheck, Trash2, Copy, Clock, ExternalLink, PackagePlus, FileText, Wrench, Percent, Lock, CreditCard } from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { tautanProgram, RUTE_DASHBOARD_PP } from "./finance/CashbackProgram";
import ModalBatalValidasi, { sayaPengajuBatal } from "./finance/ModalBatalValidasi";

/** Pusat Approval (brief bos #23) — satu pintu baca-saja untuk memantau
 *  semua pengajuan yang menunggu persetujuan lintas modul: cuti/SKD, review
 *  absensi, finance (transaksi/piutang/utang), reimbursement, opname
 *  persediaan, dan pengajuan hapus (PIN). Auto-refresh tiap 30 detik.
 *  Kontrak backend: GET /api/approval-center (lihat backend/routers/aktivitas.py).
 *  Satu pengecualian baca-saja: pengajuan batal validasi piutang bisa langsung
 *  disetujui/ditolak di sini (keputusan owner 23 Sep, B1). */

const AUTO_REFRESH_MS = 30000;

function sisaWaktu(kedaluwarsa, terlewat) {
  if (terlewat) return "Sudah kedaluwarsa";
  if (!kedaluwarsa) return "-";
  const diffMs = new Date(kedaluwarsa).getTime() - Date.now();
  if (diffMs <= 0) return "Sudah kedaluwarsa";
  const menit = Math.ceil(diffMs / 60000);
  return `${menit} menit lagi`;
}

async function salinPin(pin) {
  try {
    await navigator.clipboard.writeText(pin);
    toast.success("PIN disalin ke clipboard");
  } catch {
    toast.error("Gagal menyalin PIN — salin manual.");
  }
}

/* ---- Kartu ringkasan jumlah per kelompok ---- */
function RingkasanCard({ tid, icon: Icon, label, jumlah, tint = "slate" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 shadow-sm" data-testid={`ac-ringkasan-${tid}`}>
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-xl font-bold text-slate-900">{jumlah}</p>
    </div>
  );
}

/* ---- Blok daftar per kelompok (generik, dipakai untuk 5 dari 6 kelompok) ---- */
function GroupSection({ nama, label, icon: Icon, tint = "slate", jumlah, daftar, linkTo, emptyText, renderRow, keyFn }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 md:p-5" data-testid={`ac-grup-${nama}`}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-slate-900 text-sm md:text-base">{label}</h3>
            <p className="text-xs text-slate-500">{jumlah} menunggu</p>
          </div>
        </div>
        {linkTo && (
          <Link to={linkTo} data-testid={`ac-link-${nama}`}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
            Buka halaman <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      {jumlah === 0 ? (
        <div className="text-center py-6 text-xs text-slate-400">{emptyText || "Tidak ada yang menunggu"}</div>
      ) : (
        <div className="max-h-72 overflow-y-auto pr-1">
          {daftar.map((it, i) => (
            <div key={keyFn ? keyFn(it, i) : (it.id || i)}>{renderRow(it)}</div>
          ))}
        </div>
      )}
    </section>
  );
}

const rowCls = "flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-sm";

function renderCuti(it) {
  return (
    <div className={rowCls} data-testid={`ac-row-cuti-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">{it.employee_nama}</p>
        <p className="text-xs text-slate-500 truncate">
          {it.jenis}{it.use_skd ? " · SKD" : ""} · {it.tanggal_mulai} → {it.tanggal_selesai} · {it.jml_hari} hari
        </p>
      </div>
      <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
        {it.status === "pending_hr" ? "Menunggu HR" : "Menunggu Manager"}
      </span>
    </div>
  );
}

function renderAbsensi(it) {
  return (
    <div className={rowCls} data-testid={`ac-row-absensi-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">{it.employee_nama}</p>
        <p className="text-xs text-slate-500 truncate">
          {it.date}{it.late_minutes ? ` · Telat ${it.late_minutes} menit` : ""}
        </p>
      </div>
      <div className="shrink-0 flex gap-1 flex-wrap justify-end">
        {(it.flags || []).slice(0, 2).map((f, i) => (
          <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium">{f}</span>
        ))}
      </div>
    </div>
  );
}

function renderFinance(it) {
  const pihak = it.pelanggan_nama || it.supplier_nama || "";
  return (
    <div className={rowCls} data-testid={`ac-row-finance-${it.tipe}-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {it.kategori || "-"} <span className="text-slate-400 font-normal">· {it.tipe}</span>
        </p>
        <p className="text-xs text-slate-500 truncate">{pihak}{pihak ? " · " : ""}{it.tanggal}</p>
      </div>
      <span className="shrink-0 font-mono text-xs font-semibold text-slate-900">{idr(it.nominal)}</span>
    </div>
  );
}

function renderReimbursement(it) {
  return (
    <div className={rowCls} data-testid={`ac-row-reimbursement-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {it.claimer_name} <span className="text-slate-400 font-normal">· {it.code}</span>
        </p>
        <p className="text-xs text-slate-500 truncate">{it.kategori_beban_name || "-"} · {it.tanggal}</p>
      </div>
      <span className="shrink-0 font-mono text-xs font-semibold text-slate-900">{idr(it.nominal)}</span>
    </div>
  );
}

/* ---- Brief 4 Sep 2026 Batch 2: tiga antrean gudang (aditif) ---- */
function renderRequestStok(it) {
  return (
    <div className={rowCls} data-testid={`ac-row-request-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {it.code} <span className="text-slate-400 font-normal">· {it.warehouse_tujuan_nama || "-"}</span>
        </p>
        <p className="text-xs text-slate-500 truncate">{it.requested_by_name || "-"} · {it.tanggal}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-mono text-xs font-semibold text-slate-700">{it.jumlah_item} SKU</p>
        <p className="text-[10px] text-slate-500">{it.qty_total} unit</p>
      </div>
    </div>
  );
}

function renderMemo(it) {
  return (
    <div className={rowCls} data-testid={`ac-row-memo-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {it.judul || "-"} <span className="text-slate-400 font-normal">· {it.nomor}</span>
        </p>
        <p className="text-xs text-slate-500 truncate">{it.dibuat_oleh_nama || "-"} · {it.tanggal}</p>
      </div>
    </div>
  );
}

function renderPenyesuaianUnit(it) {
  return (
    <div className={rowCls} data-testid={`ac-row-psu-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {it.code} <span className="text-slate-400 font-normal">· {it.tindakan} · {it.warehouse_nama || "-"}</span>
        </p>
        <p className="text-xs text-slate-500 truncate">{it.diajukan_oleh_nama || "-"} · {it.tanggal}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-mono text-xs font-semibold text-slate-700">{it.jumlah_unit} unit</p>
      </div>
    </div>
  );
}

/* ---- Brief 22 Sep 2026 (A7): pengajuan Selesai Program Price Protection ----
 * Hanya MENDAFTAR. Setujui/tolak tetap di jendela program (tautan baris), dan
 * `boleh_setujui` dihitung backend dengan penjaga yang sama dengan endpoint-nya
 * (`cashback_mesin.boleh_setujui_selesai`) — layar tidak menebak siapa PIC. */
function renderPriceProtectionSelesai(it) {
  return (
    <Link to={tautanProgram(it.modul, it.id)} className={`${rowCls} hover:bg-slate-50 -mx-2 px-2 rounded`} data-testid={`ac-row-pp-selesai-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {it.nama_program || "-"} <span className="text-slate-400 font-normal">· {it.code}</span>
        </p>
        <p className="text-xs text-slate-500 truncate">
          {it.judul} · diajukan {it.diajukan_oleh_nama || "-"}{it.pic_nama ? ` · PIC ${it.pic_nama}` : ""}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-mono text-xs font-semibold text-slate-900">{idr(it.total_eligible || 0)}</p>
        <p className={`text-[10px] ${it.boleh_setujui ? "text-emerald-700 font-medium" : "text-slate-500"}`}>
          {it.boleh_setujui ? "Anda bisa menyetujui" : "Menunggu PIC / SPV"}
        </p>
      </div>
    </Link>
  );
}

function renderOpname(it) {
  const naik = Number(it.selisih) >= 0;
  return (
    <div className={rowCls} data-testid={`ac-row-opname-${it.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {it.nama_produk || it.sku} <span className="text-slate-400 font-normal">· {it.code}</span>
        </p>
        <p className="text-xs text-slate-500 truncate">{it.sku} · {it.tanggal}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className={`font-mono text-xs font-semibold ${naik ? "text-emerald-700" : "text-rose-700"}`}>
          {naik ? "+" : ""}{it.selisih}
        </p>
        <p className="text-[10px] text-slate-500">{idr(it.nilai_selisih)}</p>
      </div>
    </div>
  );
}

/* ---- Brief 22 Sep 2026 B1: pengajuan batal validasi piutang ----
   Keputusan 23 Sep B1: SPV menyetujui/menolak dari layar Piutang DAN di sini.
   Tombol Setujui/Tolak memakai modal & endpoint yang SAMA dengan layar Piutang
   (`finance/ModalBatalValidasi.jsx`) dan hanya tampil untuk pemegang
   `finance_piutang.delete` yang BUKAN pengajunya — persis gerbang server
   (`piutang.py::KUNCI_SPV_BATAL_VALIDASI` + `_orang_yang_sama`); tombol yang
   ujungnya 403 tidak ditampilkan (aturan 3). Tautan ke halaman Piutang
   berlabel "Lihat" dan hanya untuk yang boleh membukanya. */
function renderBatalValidasi({ bolehBuka, bolehPutuskan, user, onPutuskan }) {
  return function Baris(it) {
    const pengajuan = { oleh: it.diajukan_oleh, email: it.diajukan_email,
                        user_id: it.diajukan_user_id, alasan: it.alasan };
    const bisaPutuskan = bolehPutuskan && !sayaPengajuBatal(pengajuan, user);
    const piutang = {
      id: it.id, code: it.code, pelanggan_nama: it.pelanggan_nama, nominal: it.nominal,
      validasi_oleh: it.divalidasi_oleh, validasi_pada: it.divalidasi_pada,
      validasi_pengajuan_batal: pengajuan,
    };
    return (
      <div className={rowCls} data-testid={`ac-row-batal-validasi-${it.id}`}>
        <div className="min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {it.pelanggan_nama || "-"} <span className="text-slate-400 font-normal">· {it.code}</span>
          </p>
          <p className="text-xs text-slate-500 truncate">
            Diajukan {it.diajukan_oleh || "-"}: {it.alasan || "-"}
          </p>
        </div>
        <div className="shrink-0 text-right space-y-1">
          <p className="font-mono text-xs font-semibold text-slate-900">{idr(it.nominal)}</p>
          {bisaPutuskan ? (
            <div className="flex gap-1 justify-end">
              <button type="button" onClick={() => onPutuskan({ mode: "setujui", piutang })}
                data-testid={`ac-setujui-batal-validasi-${it.id}`}
                className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                Setujui
              </button>
              <button type="button" onClick={() => onPutuskan({ mode: "tolak", piutang })}
                data-testid={`ac-tolak-batal-validasi-${it.id}`}
                className="text-[10px] px-2 py-0.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50">
                Tolak
              </button>
            </div>
          ) : bolehPutuskan ? (
            <p className="text-[10px] text-slate-400">menunggu SPV lain</p>
          ) : null}
          {bolehBuka && (
            <Link to={`/finance/piutang?validasi=menunggu_batal&highlight=${it.id}`}
              data-testid={`ac-buka-batal-validasi-${it.id}`}
              className="text-[11px] text-emerald-700 hover:underline">Lihat</Link>
          )}
        </div>
      </div>
    );
  };
}

/* ---- Brief 22 Sep 2026 C7: pengajuan batal klaim MDR "Sudah Claim" ----
   Keputusan tetap di layar Claim MDR (penjaga `boleh_setujui_batal` yang sama
   dengan endpoint-nya); di sini hanya daftar + tautan. Backend sudah
   mengosongkan blok ini untuk yang tidak punya izin `finance_claim_mdr.view`
   (nama konsumen & IMEI kredit), tautannya tetap digerbang `can()` yang sama. */
function renderBatalClaimMdr(bolehBuka) {
  return function Baris(it) {
    const r = it.ringkas || {};
    return (
      <div className={rowCls} data-testid={`ac-row-batal-claim-mdr-${it.id}`}>
        <div className="min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {r.nama_konsumen || "-"} <span className="text-slate-400 font-normal">· {r.imei || "-"} · {it.tab}</span>
          </p>
          <p className="text-xs text-slate-500 truncate">
            Diajukan {it.pengajuan_batal?.oleh_nama || it.pengajuan_batal?.oleh_email || "-"}: {it.pengajuan_batal?.alasan || "-"}
          </p>
          {it.cn_batch?.hidup && (
            <p className="text-[11px] text-amber-700 truncate" data-testid={`ac-batal-claim-mdr-cn-${it.id}`}>
              Batch sudah ber-CN {it.cn_batch.cn_code} {idr(it.cn_batch.nilai)} — sesuaikan CN bila unit ini termasuk nilainya
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="font-mono text-xs font-semibold text-slate-900">{idr(r.nominal)}</p>
          {bolehBuka && it.modul && (
            <Link to={it.modul} data-testid={`ac-buka-batal-claim-mdr-${it.id}`}
              className="text-[11px] text-emerald-700 hover:underline">{it.boleh_setujui ? "Putuskan" : "Lihat"}</Link>
          )}
        </div>
      </div>
    );
  };
}

/* ---- Kelompok "Pengajuan Hapus" — bespoke karena PIN besar + salin ---- */
function HapusSection({ data }) {
  const jumlah = data?.jumlah || 0;
  const daftar = data?.daftar || [];
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 md:p-5" data-testid="ac-grup-hapus_aktivitas">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg grid place-items-center bg-rose-50 text-rose-600 shrink-0">
          <Trash2 className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-display font-bold text-slate-900 text-sm md:text-base">Pengajuan Hapus</h3>
          <p className="text-xs text-slate-500">{jumlah} menunggu PIN persetujuan</p>
        </div>
      </div>

      {!data?.pin_terlihat && jumlah > 0 && (
        <div className="mb-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="ac-pin-tersembunyi-note">
          PIN hanya tampil untuk akun pemegang izin "Pusat Approval → Beri Izin Hapus". Anda dapat melihat daftar pengajuannya, tapi PIN-nya disembunyikan.
        </div>
      )}

      {jumlah === 0 ? (
        <div className="text-center py-6 text-xs text-slate-400">Tidak ada pengajuan hapus yang menunggu</div>
      ) : (
        <div className="space-y-3">
          {daftar.map((it) => (
            <div key={it.id}
              className={`rounded-lg border p-3 ${it.kedaluwarsa_terlewat ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50/40"}`}
              data-testid={`ac-row-hapus-${it.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 text-sm">{it.pemohon_nama || it.pemohon}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Modul <b className="text-slate-700">{it.modul}</b> · Target <span className="font-mono">{it.target_id}</span>
                  </p>
                  {it.ringkasan && <p className="text-xs text-slate-600 mt-1">{it.ringkasan}</p>}
                  <p className={`text-[11px] mt-1 inline-flex items-center gap-1 ${it.kedaluwarsa_terlewat ? "text-slate-400" : "text-amber-700 font-medium"}`}>
                    <Clock className="w-3 h-3" /> {sisaWaktu(it.kedaluwarsa, it.kedaluwarsa_terlewat)}
                  </p>
                </div>
                <div className="shrink-0">
                  {it.pin ? (
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">PIN Persetujuan</p>
                      <div className="flex items-center gap-2">
                        <span data-testid={`ac-pin-${it.id}`}
                          className="font-mono text-2xl font-bold tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
                          {it.pin}
                        </span>
                        <button type="button" onClick={() => salinPin(it.pin)} data-testid={`ac-pin-copy-${it.id}`}
                          title="Salin PIN" aria-label="Salin PIN"
                          className="w-9 h-9 rounded-lg border border-slate-200 grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                      <ShieldOff className="w-3.5 h-3.5" /> PIN disembunyikan
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ApprovalCenter() {
  const { can, user } = useAuth();
  const bolehLihat = can("approval_center", "view");
  // Kunci yang SAMA dengan gerbang halaman Piutang (`_bisa_lihat_piutang`).
  const bolehBukaPiutang = can("finance_piutang", "view");
  // Kunci SPV batal validasi — SAMA dengan `piutang.py::KUNCI_SPV_BATAL_VALIDASI`
  // dan layar Piutang (`bolehPutuskanBatal`).
  const bolehPutuskanBatal = can("finance_piutang", "delete");
  const [putusBatal, setPutusBatal] = useState(null); // {mode, piutang}
  const bolehBukaClaimMdr = can("finance_claim_mdr", "view");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    if (!bolehLihat) { setLoading(false); return; }
    (isRefresh ? setRefreshing : setLoading)(true);
    try {
      const r = await api.get("/approval-center");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat Pusat Approval.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [bolehLihat]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh tiap 30 detik selagi halaman terbuka.
  useEffect(() => {
    if (!bolehLihat) return;
    const timer = setInterval(() => load(true), AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [bolehLihat, load]);

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="ac-tidak-punya-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke Pusat Approval</p>
          <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin modul "Pusat Approval".</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="approval-center-page">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Admin</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" /> Pusat Approval
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Satu pintu untuk memantau semua pengajuan yang menunggu persetujuan lintas modul. Auto-segar tiap 30 detik.
          </p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={refreshing || loading} data-testid="ac-refresh"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Menyegarkan..." : "Segarkan"}
        </button>
      </div>

      <LoadingState
        loading={loading}
        error={!loading && !data ? "Gagal memuat data." : null}
        onRetry={load}
        data-testid="ac-loading"
      >
        {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <RingkasanCard tid="cuti" icon={CalendarDays} label="Cuti" jumlah={data.cuti.jumlah} tint="sky" />
            <RingkasanCard tid="absensi_review" icon={Camera} label="Review Absensi" jumlah={data.absensi_review.jumlah} tint="amber" />
            <RingkasanCard tid="finance_approval" icon={Landmark} label="Finance" jumlah={data.finance_approval.jumlah} tint="emerald" />
            <RingkasanCard tid="reimbursement" icon={HandCoins} label="Reimbursement" jumlah={data.reimbursement.jumlah} tint="amber" />
            <RingkasanCard tid="opname_persediaan" icon={PackageCheck} label="Opname" jumlah={data.opname_persediaan.jumlah} tint="slate" />
            <RingkasanCard tid="hapus_aktivitas" icon={Trash2} label="Hapus" jumlah={data.hapus_aktivitas.jumlah} tint="rose" />
          </div>
          <p className="text-xs text-slate-500" data-testid="ac-total-pending">
            Total <b className="text-slate-700">{data.total_pending}</b> pengajuan menunggu persetujuan.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GroupSection nama="cuti" label="Pengajuan Cuti" icon={CalendarDays} tint="sky"
              jumlah={data.cuti.jumlah} daftar={data.cuti.daftar} linkTo="/cuti"
              emptyText="Tidak ada pengajuan cuti/SKD yang menunggu" renderRow={renderCuti} />
            <GroupSection nama="absensi_review" label="Review Absensi" icon={Camera} tint="amber"
              jumlah={data.absensi_review.jumlah} daftar={data.absensi_review.daftar} linkTo="/attendance-review"
              emptyText="Tidak ada absensi yang perlu ditinjau" renderRow={renderAbsensi} />
            <GroupSection nama="finance_approval" label="Approval Finance" icon={Landmark} tint="emerald"
              jumlah={data.finance_approval.jumlah} daftar={data.finance_approval.daftar} linkTo="/finance/approval"
              emptyText="Tidak ada transaksi finance yang menunggu" renderRow={renderFinance}
              keyFn={(it, i) => `${it.tipe}-${it.id || i}`} />
            <GroupSection nama="reimbursement" label="Reimbursement" icon={HandCoins} tint="amber"
              jumlah={data.reimbursement.jumlah} daftar={data.reimbursement.daftar} linkTo="/finance/reimbursement"
              emptyText="Tidak ada klaim reimbursement yang menunggu" renderRow={renderReimbursement} />
            <GroupSection nama="opname_persediaan" label="Opname Persediaan" icon={PackageCheck} tint="slate"
              jumlah={data.opname_persediaan.jumlah} daftar={data.opname_persediaan.daftar} linkTo="/finance/persediaan"
              emptyText="Tidak ada opname yang menunggu" renderRow={renderOpname} />
            {/* Brief 4 Sep 2026 Batch 2 — blok baru dibaca dengan `?.` supaya backend
                lama (tanpa key ini) tidak merobohkan halaman. */}
            {data.request_stok_approval && (
              <GroupSection nama="request_stok_approval" label="Request Stok (Staf Gudang)" icon={PackagePlus} tint="emerald"
                jumlah={data.request_stok_approval.jumlah} daftar={data.request_stok_approval.daftar} linkTo="/request-stok"
                emptyText="Tidak ada request stok yang menunggu" renderRow={renderRequestStok} />
            )}
            {data.memo_approval && (
              <GroupSection nama="memo_approval" label="Internal Memo" icon={FileText} tint="slate"
                jumlah={data.memo_approval.jumlah} daftar={data.memo_approval.daftar} linkTo="/internal-memo"
                emptyText="Tidak ada memo yang menunggu approval" renderRow={renderMemo} />
            )}
            {data.penyesuaian_unit_approval && (
              <GroupSection nama="penyesuaian_unit_approval" label="Penyesuaian Unit IMEI" icon={Wrench} tint="amber"
                jumlah={data.penyesuaian_unit_approval.jumlah} daftar={data.penyesuaian_unit_approval.daftar} linkTo="/inventory/stok?tab=unit"
                emptyText="Tidak ada penyesuaian unit yang menunggu" renderRow={renderPenyesuaianUnit} />
            )}
            {data.price_protection_selesai && (
              <GroupSection nama="price_protection_selesai" label="Program Price Protection — Pengajuan Selesai" icon={Percent} tint="emerald"
                jumlah={data.price_protection_selesai.jumlah} daftar={data.price_protection_selesai.daftar}
                linkTo={data.price_protection_selesai.modul || RUTE_DASHBOARD_PP}
                emptyText="Tidak ada program yang menunggu persetujuan Selesai" renderRow={renderPriceProtectionSelesai} />
            )}
            {data.batal_validasi_piutang && (
              <GroupSection nama="batal_validasi_piutang" label="Batal Validasi Piutang" icon={Lock} tint="violet"
                jumlah={data.batal_validasi_piutang.jumlah} daftar={data.batal_validasi_piutang.daftar}
                linkTo={bolehBukaPiutang ? "/finance/piutang?validasi=menunggu_batal" : null}
                emptyText="Tidak ada pengajuan batal validasi yang menunggu"
                renderRow={renderBatalValidasi({ bolehBuka: bolehBukaPiutang,
                                                 bolehPutuskan: bolehPutuskanBatal, user,
                                                 onPutuskan: setPutusBatal })} />
            )}
            {data.batal_claim_mdr && (
              <GroupSection nama="batal_claim_mdr" label="Batal Claim MDR (Sudah Claim)" icon={CreditCard} tint="rose"
                jumlah={data.batal_claim_mdr.jumlah} daftar={data.batal_claim_mdr.daftar}
                linkTo={bolehBukaClaimMdr ? data.batal_claim_mdr.modul : null}
                emptyText="Tidak ada pengajuan batal Claim MDR yang menunggu"
                renderRow={renderBatalClaimMdr(bolehBukaClaimMdr)} />
            )}
            <HapusSection data={data.hapus_aktivitas} />
          </div>
        </>
        )}
      </LoadingState>
      {putusBatal && (
        <ModalBatalValidasi mode={putusBatal.mode} piutang={putusBatal.piutang}
          onBatal={() => setPutusBatal(null)}
          onSelesai={() => { setPutusBatal(null); load(true); }} />
      )}
    </div>
  );
}
